import { useMemo, useRef, useState } from 'react'
import {
  TrayEmptyState,
  TrayFiltersSection,
  TrayLoadingSkeleton,
  TrayResultsToolbar,
  TrayScrollRoot,
  useAnyDropdownOpen,
  useControllableValue,
} from '@components/app/panels/right-panel/tray'
import { usePhotoPlacementOverrides } from '@/api/hooks/use-photo-placement-overrides'
import { usePortraitCustomCategoriesQuery } from '@/api/hooks/use-portrait-custom-categories'
import { usePortraitGradesQuery } from '@/api/hooks/use-portrait-grades'
import { usePortraitHomeroomsQuery } from '@/api/hooks/use-portrait-homerooms'
import { usePortraitStudentsQuery } from '@/api/hooks/use-portrait-students'
import { useTrayLazyWindow } from '@/hooks/use-tray-lazy-window'
import type { PortraitStudent } from '@/api/types/portrait'
import { filterPortraitStudentsByPlaced } from '@/lib/portraits/portrait-placed-filter'
import { applyPortraitPlacementOverrides } from '@/lib/portraits/portrait-student-placement'
import { suggestPortraitStudentNames } from '@/lib/portraits/portrait-student-names'
import type { PortraitsPlacedFilter } from '@/lib/portraits/types/portraits-placed-filter'
import { PortraitsGallery } from './components/portraits-gallery'
import { PortraitsTrayFilters } from './components/portraits-tray-filters'
import { PORTRAIT_NAME_SUGGESTION_LIMIT } from './constants/portrait-name-suggestion-limit'
import { usePortraitFilterOptions } from './hooks/use-portrait-filter-options'
import { usePortraitPlacementRefresh } from './hooks/use-portrait-placement-refresh'
import { knownSelection } from './helpers/known-selection'
import { customCategoryToOption, gradeToOption, homeroomToOption } from './helpers/to-filter-option'

// Stable empty roster: `data = []` would mint a new array every render while nothing has loaded,
// and the Placed filter's memo depends on it.
const NO_STUDENTS: PortraitStudent[] = []

type PortraitsPanelProps = {
  /** Reports "is one of my dropdowns open" so `RightPanel` can run its Escape-key priority chain
   *  without owning this panel's state — mirrors `BackgroundsPanel`'s `onAnyDropdownOpenChange`. */
  onAnyDropdownOpenChange?: (open: boolean) => void
  /** Controlled filters (name search, grade, homeroom, custom group, Placed) — let a host
   *  (`right-panel/root-panel/hooks/use-portraits-filters.ts`) keep them across this panel's own
   *  unmount when the right panel collapses or switches to a different rail icon (WMPCM-11026
   *  AC-8, as Templates and ClikArt do). Each falls back to internal state when omitted, so the
   *  component still works standalone (e.g. in tests). The Filters section's expanded state is not
   *  lifted, matching Templates. */
  nameQuery?: string
  onNameQueryChange?: (query: string) => void
  gradeId?: string
  onGradeIdChange?: (gradeId: string) => void
  homeroomId?: string
  onHomeroomIdChange?: (homeroomId: string) => void
  customId?: string
  onCustomIdChange?: (customId: string) => void
  placedFilter?: PortraitsPlacedFilter
  onPlacedFilterChange?: (placed: PortraitsPlacedFilter) => void
}

export function PortraitsPanel({
  onAnyDropdownOpenChange,
  nameQuery: nameQueryProp,
  onNameQueryChange,
  gradeId: gradeIdProp,
  onGradeIdChange,
  homeroomId: homeroomIdProp,
  onHomeroomIdChange,
  customId: customIdProp,
  onCustomIdChange,
  placedFilter: placedFilterProp,
  onPlacedFilterChange,
}: PortraitsPanelProps = {}) {
  const scrollRootRef = useRef<HTMLDivElement>(null)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [nameQuery, setNameQuery] = useControllableValue(nameQueryProp, onNameQueryChange, '')
  const [pickedGradeId, setGradeId] = useControllableValue(gradeIdProp, onGradeIdChange, '')
  const [pickedHomeroomId, setHomeroomId] = useControllableValue(
    homeroomIdProp,
    onHomeroomIdChange,
    ''
  )
  const [pickedCustomId, setCustomId] = useControllableValue(customIdProp, onCustomIdChange, '')
  const [placedFilter, setPlacedFilter] = useControllableValue<PortraitsPlacedFilter>(
    placedFilterProp,
    onPlacedFilterChange,
    'any'
  )

  const reportAnyDropdownOpenChange = useAnyDropdownOpen(
    { name: false, grade: false, homeroom: false, custom: false },
    onAnyDropdownOpenChange
  )

  // A dropdown's list is only requested once Filters is open, or when a chosen value needs its name
  // shown (the choices outlive the panel, so one can be in force while Filters is collapsed).
  const gradesQuery = usePortraitGradesQuery({ enabled: filtersOpen || pickedGradeId !== '' })
  const homeroomsQuery = usePortraitHomeroomsQuery({
    enabled: filtersOpen || pickedHomeroomId !== '',
  })
  const customCategoriesQuery = usePortraitCustomCategoriesQuery({
    enabled: filtersOpen || pickedCustomId !== '',
  })
  const grades = usePortraitFilterOptions(gradesQuery, gradeToOption)
  const homerooms = usePortraitFilterOptions(homeroomsQuery, homeroomToOption)
  const customCategories = usePortraitFilterOptions(customCategoriesQuery, customCategoryToOption)

  // A choice missing from its loaded list reads as unset (its field shows the placeholder), so it
  // is neither sent to the API nor counted.
  const gradeId = knownSelection(pickedGradeId, grades)
  const homeroomId = knownSelection(pickedHomeroomId, homerooms)
  const customId = knownSelection(pickedCustomId, customCategories)

  // Fuzzy matching and closest-first ordering happen server-side (see `portraits.service.ts`), so
  // the rows arrive in the order the gallery shows them. The session's placements and the Placed
  // filter narrow them below, and the gallery and the suggestions use the result.
  const {
    data: roster = NO_STUDENTS,
    isLoading,
    isError,
    isSearchCurrent,
  } = usePortraitStudentsQuery({
    searchName: nameQuery,
    gradeId,
    homeroomId,
    customId,
  })

  // The server's placed flag is only as fresh as its last indexing, so what this session has
  // placed or removed on a spread is laid over it, as the ClikArt tray does, and the roster is
  // re-fetched (debounced) after a change so the server catches up. A placed portrait is a photo
  // fill, so the photo placement session already tracks it on every canvas edit.
  const placementOverrides = usePhotoPlacementOverrides()

  usePortraitPlacementRefresh(placementOverrides)

  const placementRoster = useMemo(
    () => applyPortraitPlacementOverrides(roster, placementOverrides),
    [roster, placementOverrides]
  )

  // Placed is applied here, over the rows the server sent, not by the server: every row carries
  // the student-level `studentUsedOnPage` flag, so Any / Yes / No re-filter at once with no
  // request, and the gallery, the suggestions and the count all see the same students.
  const students = useMemo(
    () => filterPortraitStudentsByPlaced(placementRoster, placedFilter),
    [placementRoster, placedFilter]
  )

  const hasNameQuery = nameQuery.trim() !== ''
  const nameSuggestions = useMemo(
    () =>
      hasNameQuery && isSearchCurrent
        ? suggestPortraitStudentNames(students, PORTRAIT_NAME_SUGGESTION_LIMIT)
        : [],
    [hasNameQuery, isSearchCurrent, students]
  )

  // A non-empty search, a chosen grade, homeroom or custom group and a Placed other than Any are
  // one active filter each.
  const activeFilterCount = [
    hasNameQuery,
    gradeId !== '',
    homeroomId !== '',
    customId !== '',
    placedFilter !== 'any',
  ].filter(Boolean).length

  function handleClearFilters() {
    setNameQuery('')
    setGradeId('')
    setHomeroomId('')
    setCustomId('')
    setPlacedFilter('any')
  }

  // A whole school's roster can be hundreds of rows, so only a window of them is in the page.
  const {
    visibleItems: visibleStudents,
    hasMore,
    sentinelRef,
  } = useTrayLazyWindow({
    items: students,
    scrollRootRef,
    resetKey: JSON.stringify([nameQuery, gradeId, homeroomId, customId, placedFilter]),
  })

  const resultCount = students.length
  const showResultCount = !isLoading && !isError

  return (
    <TrayScrollRoot scrollRootRef={scrollRootRef}>
      <div className="flex w-full min-w-0 shrink-0 flex-col gap-3 pb-2">
        <TrayFiltersSection
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
          activeCount={activeFilterCount}
          onClear={handleClearFilters}
        >
          <PortraitsTrayFilters
            nameQuery={nameQuery}
            onNameQueryChange={setNameQuery}
            nameSuggestions={nameSuggestions}
            onNameSuggestionsOpenChange={reportAnyDropdownOpenChange('name')}
            grade={{
              ...grades,
              value: gradeId,
              onChange: setGradeId,
              onOpenChange: reportAnyDropdownOpenChange('grade'),
            }}
            homeroom={{
              ...homerooms,
              value: homeroomId,
              onChange: setHomeroomId,
              onOpenChange: reportAnyDropdownOpenChange('homeroom'),
            }}
            custom={{
              ...customCategories,
              value: customId,
              onChange: setCustomId,
              onOpenChange: reportAnyDropdownOpenChange('custom'),
            }}
            placedFilter={placedFilter}
            onPlacedFilterChange={setPlacedFilter}
          />
        </TrayFiltersSection>

        <TrayResultsToolbar count={resultCount} showCount={showResultCount} />
      </div>

      {isLoading ? (
        <TrayLoadingSkeleton label="Loading portraits" />
      ) : isError ? (
        <p className="px-1 py-4 text-sm text-destructive">Unable to load portraits.</p>
      ) : resultCount === 0 ? (
        <TrayEmptyState
          title="No results found"
          description="Try adjusting your search or filters."
        />
      ) : (
        <PortraitsGallery students={visibleStudents} sentinelRef={sentinelRef} hasMore={hasMore} />
      )}
    </TrayScrollRoot>
  )
}
